import { useState } from 'react';
import {
  getUpcomingRfwEvents,
  getPopularEvents,
  getTrendingRfwEvents,
  getClubExclusiveEvents,
  getTimeRemainingText,
  formatEventDate,
  formatCounter,
  eventTypeLabels,
  eventTypeIcons,
  eventTypeDotColors,
  eventTypeTextColors,
  eventDashboardStats,
  type RfwEvent,
} from '@/mocks/rfwEvents';

function CompactEventCard({ event, showClubBadge }: { event: RfwEvent; showClubBadge?: boolean }) {
  const days = getTimeRemainingText(event.date);
  const isToday = days === 'Hoy';

  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 group cursor-pointer">
      <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-background-100">
        <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          {isToday && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
              HOY
            </span>
          )}
          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${event.type === 'presencial' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : event.type === 'virtual' ? 'bg-sky-50 text-sky-700 border-sky-200' : event.type === 'sorteo' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {eventTypeLabels[event.type]}
          </span>
          {showClubBadge && event.isClubExclusive && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 font-medium">Club</span>
          )}
        </div>
        <p className="text-xs font-semibold text-foreground-900 leading-snug mb-0.5 line-clamp-2">{event.title}</p>
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line text-[9px]"></i>
            {days}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line text-[9px]"></i>
            {formatCounter(event.counters.views)}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-share-forward-line text-[9px]"></i>
            {formatCounter(event.counters.shared)}
          </span>
        </div>
      </div>
    </div>
  );
}

function FeaturedEventCard({ event }: { event: RfwEvent }) {
  const days = getTimeRemainingText(event.date);
  const isToday = days === 'Hoy';

  return (
    <div className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-300/60 transition-all duration-300 cursor-pointer">
      <div className="relative h-40 overflow-hidden">
        <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          {isToday && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              HOY
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${event.type === 'concierto' ? 'bg-rose-500/80 text-white' : event.type === 'festival' ? 'bg-orange-500/80 text-white' : 'bg-white/20 text-white backdrop-blur-sm'}`}>
            {eventTypeLabels[event.type]}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-bold text-sm leading-snug line-clamp-2">{event.title}</p>
          <p className="text-white/70 text-[10px] mt-0.5">{event.stationName} · {event.city}</p>
        </div>
      </div>
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line text-[9px]"></i>
            {days}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-group-line text-[9px]"></i>
            {formatCounter(event.counters.interested)}
          </span>
        </div>
        <span className="text-[10px] font-medium text-foreground-400">{event.price}</span>
      </div>
    </div>
  );
}

export default function EventosSection() {
  const [activeTab, setActiveTab] = useState<'proximos' | 'populares' | 'tendencia' | 'club'>('proximos');

  const proximos = getUpcomingRfwEvents().slice(0, 6);
  const populares = getPopularEvents();
  const tendencia = getTrendingRfwEvents();
  const clubExclusive = getClubExclusiveEvents().slice(0, 6);

  const tabs = [
    { id: 'proximos' as const, label: 'Próximos Eventos', icon: 'ri-calendar-line', count: proximos.length },
    { id: 'populares' as const, label: 'Más Populares', icon: 'ri-fire-line', count: populares.length },
    { id: 'tendencia' as const, label: 'En Tendencia', icon: 'ri-rocket-line', count: tendencia.length },
    { id: 'club' as const, label: 'Exclusivos Club', icon: 'ri-shield-star-line', count: clubExclusive.length },
  ];

  const currentEvents = activeTab === 'proximos' ? proximos : activeTab === 'populares' ? populares : activeTab === 'tendencia' ? tendencia : clubExclusive;

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-secondary-500 animate-pulse"></span>
            SISTEMA DE EVENTOS RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Donde la Comunidad <span className="text-secondary-600">Se Encuentra</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Eventos presenciales, virtuales, conciertos, festivales, sorteos y mucho más. Todo evento puede compartirse. Todo evento genera interacción. Todo evento suma Score.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {[
            { label: 'Eventos Hoy', value: eventDashboardStats.todayEvents.toString(), icon: 'ri-calendar-check-line', color: 'text-red-600 bg-red-50' },
            { label: 'Esta Semana', value: eventDashboardStats.upcomingThisWeek.toString(), icon: 'ri-calendar-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Asistentes Totales', value: formatCounter(eventDashboardStats.totalAttendees), icon: 'ri-group-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Compartidos', value: formatCounter(eventDashboardStats.totalShared), icon: 'ri-share-forward-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-secondary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'populares' || activeTab === 'tendencia' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {(currentEvents as RfwEvent[]).slice(0, 3).map((event) => (
              <FeaturedEventCard key={event.id} event={event} />
            ))}
            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {(currentEvents as RfwEvent[]).slice(3).map((event) => (
                <CompactEventCard key={event.id} event={event} showClubBadge />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentEvents.map((event) => (
              <CompactEventCard key={event.id} event={event} showClubBadge />
            ))}
          </div>
        )}

        {/* Club Exclusive Highlight */}
        {activeTab === 'club' && (
          <div className="mt-8 p-6 rounded-2xl bg-violet-50 border border-violet-200 text-center">
            <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-shield-star-line text-xl text-violet-600"></i>
            </div>
            <p className="text-sm font-bold text-foreground-950 mb-1">Eventos Exclusivos por Club</p>
            <p className="text-xs text-foreground-600 max-w-lg mx-auto">
              Los clubes oficiales de cada radio, programa y locutor pueden organizar eventos exclusivos para sus miembros. Unite a un club y accedé a experiencias que nadie más puede vivir.
            </p>
            <a href="/clubes" className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors whitespace-nowrap">
              Explorar Clubes <i className="ri-arrow-right-line text-[10px]"></i>
            </a>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <a
            href="/eventos"
            className="inline-flex items-center gap-2 px-6 py-3 bg-secondary-500 hover:bg-secondary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-calendar-event-line"></i>
            Ver Todos los Eventos
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-xs text-foreground-500 mt-3">
            Descubrí, compartí y participá en los eventos de todas las radios RFW
          </p>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-secondary-100/70 border border-secondary-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-share-forward-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">Todo evento debe poder compartirse.</p>
          <p className="text-xs text-foreground-600">Compartir no es solo difundir — es invitar a tu comunidad a ser parte de algo más grande.</p>
        </div>
      </div>
    </section>
  );
}